import { useState, useEffect, useRef, useCallback } from 'react';
import { fiscalYearsApi } from '../services/api/endpoints/fiscalYears';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { FallbackService } from '../services/FallbackService';
import type { FiscalYear } from '../types/fiscal-year';

export function useFiscalYear() {
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false); // Prevent multiple concurrent calls
  
  const { currentFiscalYear, setCurrentFiscalYear } = useFiscalYearStore();

  const loadFiscalYears = useCallback(async () => {
    // Prevent multiple concurrent calls
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      const response = await fiscalYearsApi.getAll();
      if (response.success && response.data) {
        // Gérer le cas où la réponse est enveloppée dans { data: [...] }
        const rawData = response.data;
        const yearsData = Array.isArray(rawData) ? rawData : (rawData.data || []);
        const years = Array.isArray(yearsData) ? yearsData : [];
        setFiscalYears(years);
        
        // Définir l'exercice courant si pas déjà défini ou si l'ancien n'existe plus
        const openYear = years.find(year => year.status === 'open');
        if (!currentFiscalYear || !years.find(y => y.id === currentFiscalYear.id)) {
          setCurrentFiscalYear(openYear || years[0] || null);
        }
      } else {
        // Fallback vers les données mock si l'API échoue
        console.warn('⚠️ API fiscal years failed, trying fallback...');
        const fallbackResponse = await FallbackService.getFallbackData<FiscalYear[]>('/fiscal-years');
        
        if (fallbackResponse?.success && fallbackResponse.data) {
          const fallbackYears = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
          console.log('✅ Using fallback fiscal years:', fallbackYears.length);
          setFiscalYears(fallbackYears);
          
          const openYear = fallbackYears.find(year => year.status === 'open');
          if (!currentFiscalYear) {
            setCurrentFiscalYear(openYear || fallbackYears[0] || null);
          }
        } else {
          setError(response.error || 'Erreur lors du chargement des exercices fiscaux');
          setFiscalYears([]);
        }
      }
    } catch (err) {
      console.error('Failed to load fiscal years:', err);
      
      // Fallback en cas d'erreur réseau
      try {
        console.warn('⚠️ Network error, trying fallback...');
        const fallbackResponse = await FallbackService.getFallbackData<FiscalYear[]>('/fiscal-years');
        
        if (fallbackResponse?.success && fallbackResponse.data) {
          const fallbackYears = Array.isArray(fallbackResponse.data) ? fallbackResponse.data : [];
          console.log('✅ Using fallback fiscal years after error:', fallbackYears.length);
          setFiscalYears(fallbackYears);
          
          const openYear = fallbackYears.find(year => year.status === 'open');
          if (!currentFiscalYear) {
            setCurrentFiscalYear(openYear || fallbackYears[0] || null);
          }
          // Clear error since fallback worked
          setError(null);
        } else {
          setError('Impossible de charger les exercices fiscaux');
          setFiscalYears([]);
        }
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
        setError('Impossible de charger les exercices fiscaux');
        setFiscalYears([]);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [currentFiscalYear, setCurrentFiscalYear]);

  useEffect(() => {
    loadFiscalYears();
  }, []);

  const createFiscalYear = async (data: { startDate: string; endDate: string; code: string }): Promise<FiscalYear | null> => {
    try {
      setLoading(true);
      const response = await fiscalYearsApi.create(data);
      if (response.success && response.data) {
        // Gérer le cas où la réponse est enveloppée dans { data: {...} }
        const fiscalYearData = response.data.data || response.data;
        // Recharger la liste des exercices fiscaux
        await loadFiscalYears();
        // Définir le nouvel exercice comme exercice courant
        setCurrentFiscalYear(fiscalYearData);
        return fiscalYearData;
      } else {
        setError(response.error || 'Erreur lors de la création de l\'exercice fiscal');
        return null;
      }
    } catch (err) {
      console.error('Failed to create fiscal year:', err);
      setError('Impossible de créer l\'exercice fiscal');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const formatPeriod = (fiscalYear: FiscalYear) => {
    const start = new Date(fiscalYear.startDate);
    const end = new Date(fiscalYear.endDate);
    return `${start.toLocaleDateString('fr-FR')} - ${end.toLocaleDateString('fr-FR')}`;
  };

  const reopenFiscalYear = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      const response = await fiscalYearsApi.reopen(id);
      if (response.success && response.data) {
        // Gérer le cas où la réponse est enveloppée dans { data: {...} }
        const fiscalYearData = response.data.data || response.data;
        setCurrentFiscalYear(fiscalYearData);
        // Recharger la liste pour avoir les statuts à jour
        await loadFiscalYears();
      } else {
        setError(response.error || 'Erreur lors de la réouverture de l\'exercice fiscal');
        throw new Error(response.error || 'Erreur lors de la réouverture');
      }
    } catch (err) {
      console.error('Failed to reopen fiscal year:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const closeFiscalYear = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      const response = await fiscalYearsApi.close(id);
      if (response.success && response.data) {
        // Recharger la liste pour avoir les statuts à jour
        await loadFiscalYears();
      } else {
        setError(response.error || 'Erreur lors de la clôture de l\'exercice fiscal');
        throw new Error(response.error || 'Erreur lors de la clôture');
      }
    } catch (err) {
      console.error('Failed to close fiscal year:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const selectFiscalYear = (fiscalYear: FiscalYear) => {
    setCurrentFiscalYear(fiscalYear);
  };

  return {
    fiscalYears,
    currentFiscalYear,
    loading,
    error,
    formatPeriod,
    createFiscalYear,
    reopenFiscalYear,
    closeFiscalYear,
    selectFiscalYear,
    loadFiscalYears
  };
}